import { useEffect, useRef, useState } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { PageSpinner, useReplaceSearchParams } from "@impero-com/ui";
import { useNavigate, useLocation } from "react-router-dom";

import { DashboardPendingActivitiesContainer } from "./DashboardPendingActivitiesContainer";
import { usePendingWorkQueueRows } from "./hooks/usePendingWorkQueueRows";
import {
    handleNotifications,
    NotificationType,
    notificationTypes,
} from "./notifications";
import {
    dashboardPendingActivitiesUrl,
    FROM_DEPRECATED_PENDING_TESTS_PAGE_PARAM,
} from "./urlUtils";

export function DashboardPendingActivitiesPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const replaceSearchParams = useReplaceSearchParams();
    const hasSynced = useRef(false);
    const [arrivedFromPendingTests] = useState(() =>
        new URLSearchParams(location.search).has(
            FROM_DEPRECATED_PENDING_TESTS_PAGE_PARAM
        )
    );

    useEffect(() => {
        if (hasSynced.current) {
            return;
        }
        // 1. Check for notification param in URL or the redirect param
        // 2. If present, restore the saved view, which also drops these one-off params
        const params = new URLSearchParams(location.search);
        const hasRedirect = params.has("redirect"); // We set the redirect param when coming from Dashboard legacy page
        const hasNotification = Array.from(params.keys()).some((key) =>
            notificationTypes.includes(key as NotificationType)
        );
        if (hasNotification || hasRedirect) {
            if (hasNotification) {
                handleNotifications();
            }
            navigate(dashboardPendingActivitiesUrl, { replace: true });
        } else if (params.has(FROM_DEPRECATED_PENDING_TESTS_PAGE_PARAM)) {
            replaceSearchParams((searchParams) =>
                searchParams.delete(FROM_DEPRECATED_PENDING_TESTS_PAGE_PARAM)
            );
        }
        hasSynced.current = true;
    }, [navigate, replaceSearchParams, location.search]);

    const pendingWorkQueue = usePendingWorkQueueRows();

    switch (pendingWorkQueue.status) {
        case "pending":
            return <PageSpinner />;
        case "error":
            // Let the route ErrorBoundary render the failure
            throw pendingWorkQueue.error;
        case "success":
            return (
                <DashboardPendingActivitiesContainer
                    rows={pendingWorkQueue.rows}
                    arrivedFromPendingTests={arrivedFromPendingTests}
                />
            );
        default:
            return assertNever(pendingWorkQueue);
    }
}
